import React, { useState } from "react";
import { Block, Edit, OpenWith, Undo } from "@mui/icons-material";
import { Box, Button, Divider, Typography } from "@mui/material";

import analytics from "fond/analytics";
import { usePermissionCheck } from "fond/hooks/usePermissionCheck";
import { getCurrentProject } from "fond/project/redux";
import { useAppSelector } from "fond/utils/hooks";
import { Actions } from "fond/utils/permissions";
import { isExtendedPlannerProject } from "fond/utils/project";
import { ConfirmModal, useStackedNavigationContext } from "fond/widgets";
import { LockIcon } from "fond/widgets/Navigation/navigation.styles";

import { useDesignRefinementActions } from "./hooks/useDesignRefinementActions";

interface IProps {
  hasEditedEquipment: boolean;
  hasDropAssignments: boolean;
}

const SolveEditing: React.FC<IProps> = ({ hasEditedEquipment, hasDropAssignments }: IProps) => {
  const { open } = useStackedNavigationContext();
  const project = useAppSelector((state) => getCurrentProject(state.project));
  const isAllowed = usePermissionCheck(Actions.PLANNER_PROJECT_EDIT, project?.Permission.Level);
  const { moveHubs, editDropAssignments, resetEdits } = useDesignRefinementActions();

  const [confirmModal, setConfirmModal] = useState<"reset" | "moveHubs" | null>(null);

  const onResetEquipmentClick = () => {
    resetEdits();
    setConfirmModal(null);
  };

  const onMoveEquipmentClick = () => {
    analytics.track("Clicked move equipment");
    if (hasDropAssignments) {
      setConfirmModal("moveHubs");
    } else {
      moveHubs({ resetDropAssignments: false });
    }
  };

  const onConfirmMoveHubs = () => {
    moveHubs({ resetDropAssignments: true });
    setConfirmModal(null);
  };

  const onEditDropAssignmentsClick = () => {
    editDropAssignments();
  };

  const onBlockersClick = () => {
    open("extended-design-blockers");
  };

  return (
    <>
      {confirmModal === "reset" && (
        <ConfirmModal
          open
          content="Are you sure you want to reset all design edits?"
          onConfirm={onResetEquipmentClick}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal === "moveHubs" && (
        <ConfirmModal
          open
          content="Drop assignments must be reset to move hubs. Are you sure you want to reset your drop assignments?"
          onConfirm={onConfirmMoveHubs}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      <Box mt={2}>
        <Typography variant="h6" fontSize={14}>
          Make refinements
        </Typography>
        <Box display="flex" justifyContent="center" mt={0.5} mb={0.5}>
          {!isAllowed && <LockIcon />}
        </Box>
        <Typography variant="body3" textAlign={isAllowed ? undefined : "center"}>
          {isAllowed
            ? "The design has been successfully generated. To make modifications select an action below:"
            : "You need a license/subscription to make refinements."}
        </Typography>
        <Box my={2}>
          <Divider />
          <Button
            data-testid="reposition-hubs-button"
            fullWidth
            sx={{ my: 0.5, justifyContent: "left" }}
            startIcon={<OpenWith sx={{ height: 20, color: (theme) => theme.palette.primary.main }} />}
            onClick={onMoveEquipmentClick}
            disabled={!isAllowed}
          >
            <Typography color={(theme) => theme.palette.biarri.secondary.darkGrey} fontWeight={500} fontSize={13}>
              Move hubs
            </Typography>
          </Button>
          <Divider />
          {isExtendedPlannerProject(project) && (
            <>
              <Button
                data-testid="edit-drop-assignments-button"
                fullWidth
                sx={{ my: 0.5, justifyContent: "left" }}
                startIcon={<Edit sx={{ height: 20, color: (theme) => theme.palette.primary.main }} />}
                onClick={onEditDropAssignmentsClick}
                disabled={!isAllowed}
              >
                <Typography color={(theme) => theme.palette.biarri.secondary.darkGrey} fontWeight={500} fontSize={13}>
                  Edit drop assignments
                </Typography>
              </Button>
              <Divider />
              <Button
                data-testid="blockers-button"
                fullWidth
                sx={{ my: 0.5, justifyContent: "left" }}
                startIcon={<Block sx={{ height: 20, color: (theme) => theme.palette.primary.main }} />}
                onClick={onBlockersClick}
                disabled={!isAllowed}
              >
                <Typography color={(theme) => theme.palette.biarri.secondary.darkGrey} fontWeight={500} fontSize={13}>
                  Blockers
                </Typography>
              </Button>
              <Divider />
            </>
          )}

          {hasEditedEquipment && isAllowed && (
            <>
              <Button
                data-testid="reset-equipment-button"
                fullWidth
                sx={{ my: 0.5, justifyContent: "left", color: (theme) => theme.palette.biarri.secondary.darkGrey, fontWeight: 500, fontSize: 13 }}
                startIcon={<Undo sx={{ height: 20, color: (theme) => theme.palette.primary.main }} />}
                onClick={() => setConfirmModal("reset")}
                disabled={!isAllowed}
              >
                Reset edits
              </Button>
              <Divider />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default SolveEditing;
